import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzIconModule } from 'ng-zorro-antd/icon';
import {
  AssetsService,
  I18nService,
  Sensor,
  DateFormatSettings,
  SpinnerComponent,
} from '@lp-ui-lib';
import { SensorSubscriptionDetails } from '../../models/interfaces/sensor-subscription.interface';
import { firstValueFrom, isObservable } from 'rxjs';

@Component({
  selector: 'app-sensor-subscription',
  templateUrl: './sensor-subscription.html',
  styleUrls: ['./sensor-subscription.scss'],
  imports: [NzCollapseModule, NzIconModule, DatePipe, SpinnerComponent],
})
export class SensorSubscriptionComponent {
  private assetsService = inject(AssetsService);
  public i18n = inject(I18nService);

  readonly sensor = input<Sensor | null>(null);
  readonly dateFormat = input<DateFormatSettings | null>(null);
  subscriptionPanelActive = signal(false);
  readonly loading = signal(false);
  readonly error = signal<unknown | null>(null);
  readonly subscriptionDetails = signal<SensorSubscriptionDetails | null>(null);

  readonly dateFormatString = computed(() =>
    this.dateFormat()?.formatString === 'MM/dd/yyyy' ? 'MM-dd-yyyy' : 'dd-MM-yyyy'
  );

  private lastSensorId: number | null = null;
  private requestSeq = 0;

  constructor() {
    effect(() => {
      const s = this.sensor();
      const sensorId = s?.sensorID ? Number(s.sensorID) : null;
      if (!sensorId) {
        this.lastSensorId = null;
        this.subscriptionDetails.set(null);
        this.error.set(null);
        this.loading.set(false);
        return;
      }

      if (this.lastSensorId === sensorId) return;
      this.lastSensorId = sensorId;

      const seq = ++this.requestSeq;
      this.loading.set(true);
      this.error.set(null);
      this.subscriptionDetails.set(null);

      const result: any = this.assetsService.getSensorSubscriptionDetails(sensorId);

      const promise = isObservable(result) ? firstValueFrom(result) : Promise.resolve(result);

      promise
        .then((res: SensorSubscriptionDetails) => {
          if (seq !== this.requestSeq) return;
          this.subscriptionDetails.set(res ?? null);
        })
        .catch((err) => {
          if (seq !== this.requestSeq) return;
          console.error('[SensorSubscription] error:', err);
          this.error.set(err);
          this.subscriptionDetails.set(null);
        })
        .finally(() => {
          if (seq !== this.requestSeq) return;
          this.loading.set(false);
        });
    });
  }
}
